import React, { Fragment, useState, useEffect, useCallback, useRef, forwardRef } from 'react';
import config from '../../config';
import { GetList, AddItem, DeleteItem } from '../../REST/crud';
import { GetNoticeFields } from "../../REST/utilities";
import { useForm, Controller, useFormState } from "react-hook-form";
import TextField from '@mui/material/TextField';
import NativeSelect from '@mui/material/NativeSelect';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Slide from '@mui/material/Slide';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import PageShell from '../../themes/components/PageShell';
import PageHeader from '../../themes/components/PageHeader';
import PageContent from '../../themes/components/PageContent';
import Action from '../../themes/components/Action';
import ResponsiveDataGrid from '../../themes/components/ResponsiveDataGrid';
import WorkflowDialogShell from '../../themes/components/WorkflowDialogShell';

export default function Notices() {
    const [mode, setMode] = useState(null)
    const [fields, setFields] = useState([])
    const navigate = useNavigate()

    useEffect(() => {
        (async () => {
            const fieldsResult = await GetNoticeFields()
            setFields(fieldsResult.data)
        })()
    }, [])

    const columns = [
        { field: 'name', headerName: 'Name', flex: 0.5 },
        { field: 'module', headerName: 'Module', flex: 0.3, type: 'singleSelect', valueOptions: ['Invoices', 'WaitList', 'Company', 'Accounts', 'Messages'] },
        { field: 'trigger', headerName: 'Trigger', flex: 0.3, type: 'singleSelect', valueOptions: ['Created', 'Updated', 'Removed'] },
        {
            field: 'actions', headerName: 'Actions', flex: 0.2, renderCell: (params) => {
                return <IconButton onClick={(e) => { e.stopPropagation(); handleDelete(params.row._id) }}><DeleteIcon /></IconButton>
            }
        }
    ];

    const fetchNotices = useCallback(async (page, limit, filters, sort) => {
        const params = {
            page,
            limit
        }
        
        if (Object.keys(filters).length > 0) {
            params.filters = JSON.stringify(filters)
        }
        
        if (sort) {
            params.sort = JSON.stringify(sort)
        }
        
        const result = await GetList('Notices', params);

        if (result.data?.data) {
            return {
                data: result.data.data,
                pagination: result.data.pagination
            };
        }

        if (Array.isArray(result.data)) {
            return {
                data: result.data,
                pagination: { total: result.data.length, page: 1, totalPages: 1 }
            };
        }

        return { data: [], pagination: { total: 0, page: 1, totalPages: 0 } };
    }, []);

    const handleMode = (mode) => { setMode(mode) }

    const handleData = (data) => {
        AddItem('Notices', data).then(() => {
            setMode(null)
        })
    }

    const handleDelete = (id) => {
        DeleteItem('Notices', id).then(() => {
            // Grid will auto-refresh
        })
    }

    return <Fragment>
        <PageShell>
            <PageHeader
                title="Notices"
                back
                onBack={() => navigate(-1)}
                actions={
                    <Action
                        primary
                        icon={<AddIcon />}
                        label="Add Notice"
                        onClick={() => handleMode({})}
                    />
                }
            />

            {mode !== null && <FormDialog open={mode !== null} data={mode} handleClose={() => handleMode(null)} fields={fields} handleSave={handleData} />}

            <PageContent fill>
                <ResponsiveDataGrid
                    columns={columns}
                    fetchData={fetchNotices}
                    initialPageSize={10}
                    pageSizeOptions={[10, 25, 50, 100]}
                    getRowId={(i) => i?._id}
                />
            </PageContent>
        </PageShell>
    </Fragment>
}



function FormDialog({ open = false, data, handleClose, handleSave, fields = [] }) {
    const schema = yup.object({
        name: yup.string().trim().required(),
        module: yup.string().trim().required(),
        key: yup.string().trim().required(),
        template: yup.string().trim().required(),
        description: yup.string()
    }).required();

    const { control, handleSubmit, formState: { errors }, clearErrors, reset, getValues } = useForm({
        defaultValues: {
            name: '',
            module: '',
            key: '',
            template: '',
            description: ''
        },
        resolver: yupResolver(schema)
    });

    const { isDirty } = useFormState({ control })

    useEffect(() => {
        clearErrors()
        reset()
    }, [open, clearErrors, reset])

    const onSubmit = handleSave

    const isError = (val) => val !== undefined && val !== null && typeof val === 'object'

    const getOptions = (type) => {
        if (type === 'Modules') {
            return <Fragment>
                <option></option>
                <option>Invoices</option>
                <option>Waitlist</option>
                <option>Company</option>
            </Fragment>
        }
        return <option>NA</option>
    }

    useEffect(() => {
        reset(getValues())
    }, [isDirty, getValues, reset])

    return <WorkflowDialogShell
        open={open}
        onClose={handleClose}
        onSave={handleSubmit(onSubmit)}
        title="Notice Details"
    >
        <>
            <Controller name="name" control={control} render={({ field }) => <TextField margin='dense' error={isError(errors.name)} helperText={errors.name && errors.name.message} fullWidth label='Name' {...field} />} />
            <Controller name="description" control={control} render={({ field }) => <TextField margin='dense' error={isError(errors.description)} helperText={errors.description && errors.description.message} fullWidth label='Description' {...field} />} />
            <Controller name="key" control={control} render={({ field }) => <TextField margin='dense' error={isError(errors.key)} helperText={errors.key && errors.key.message} fullWidth label='Key' {...field} />} />
            <Controller name="module" control={control} render={({ field }) => <FormControl error={isError(errors.module)} variant='standard' margin='dense' fullWidth><InputLabel>Module</InputLabel><NativeSelect {...field}>{getOptions('Modules')}</NativeSelect>{errors.module && <FormHelperText>{errors.module.message}</FormHelperText>}</FormControl>} />
            <Controller name="template" control={control} render={({ field }) => <TemplateEditor settings={fields} errors={errors} module={getValues('module')} field={field} />} />
        </>
    </WorkflowDialogShell>
}

function TemplateEditor({ errors = [], field, settings = [], module = null }) {
    if (module !== '' && module) {
        console.log(settings, module)
        const fields = [settings.find(e => e.name === module)]

        return <DocumentEditorRef label='Template Editor' {...field} fields={fields} />
    }
    return <Fragment></Fragment>
}

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});


function DocumentEditor({ label, value, className, fields = [], onChange }, ref) {
    const [data, setData] = useState('');
    const [open, setOpen] = React.useState(false);
    const [loading, setLoading] = useState(false);
    const frameView = useRef(null);

    const handleClickOpen = () => {
        setOpen(true);
        setLoading(true);
    };

    console.log(fields)

    useEffect(() => {
        if (open === false)
            onChange(data)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    useEffect(() => {
        setData(value)
    }, [value])

    useEffect(() => {
        if (open) {
            const handleSubmit = (data) => {
                setData(data);
                setOpen(false)
            }

            const handleClose = () => {
                setOpen(false);
                setData(value)
            };

            const messageHandler = (message) => {
                console.log(message)
                if (message.origin === config.REACT_APP_TEMPLATE_URL) {
                    if (message.data === 'Initialized' && frameView.current && frameView.current.contentWindow) {
                        setLoading(false);

                        frameView.current.contentWindow.postMessage({ render: 'Fields', fields, content: data }, "*")
                    }
                    else if (message.data === 'Close')
                        handleClose()
                    else
                        handleSubmit(message.data)
                }
            }
            window.addEventListener('message', messageHandler)

            return function cleanup() {
                console.log('Cleaned')
                window.removeEventListener('message', messageHandler);
            }
        }
    }, [fields, label, data, value, open])

    return (
        <div className={className || ''}>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading} onClick={() => setLoading(false)}><CircularProgress color="inherit" /></Backdrop>
            <Button variant='text' fullWidth color="secondary" onClick={handleClickOpen}>
                {label}
            </Button>
            <Dialog fullScreen open={open} keepMounted={false} TransitionComponent={Transition}>
                <iframe src={config.REACT_APP_TEMPLATE_URL} ref={frameView}
                    frameBorder="0"
                    marginHeight="0"
                    marginWidth="0"
                    width="100%"
                    height="100%"
                    title='Template Editor'
                    scrolling="auto">
                </iframe>
            </Dialog>
        </div>
    );
}

const DocumentEditorRef = forwardRef(DocumentEditor)
